/**
 * Image utility functions for client-side image processing
 */

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

export interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Validate image file type and size
 */
export function validateImageFile(file: File): ValidationResult {
  // Check file type
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
  if (!validTypes.includes(file.type)) {
    return {
      valid: false,
      error: 'File must be JPEG or PNG format',
    };
  }

  // Check file size (5MB limit to accommodate phone photos)
  // Backend will resize to 400x400 and compress to WebP
  const maxSize = 5 * 1024 * 1024; // 5MB in bytes
  if (file.size > maxSize) {
    return {
      valid: false,
      error: 'Image must be less than 5MB',
    };
  }

  return { valid: true };
}

/**
 * Load image from File object
 */
export function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Create a cropped image from the source image
 * @param imageSrc - Source image (data URL or URL)
 * @param pixelCrop - Crop area in pixels
 * @param targetWidth - Target width for the cropped image (default 400)
 * @param targetHeight - Target height (defaults to targetWidth for square)
 * @returns Base64 data URL of cropped image
 */
export async function getCroppedImage(
  imageSrc: string,
  pixelCrop: Area,
  targetWidth: number = 400,
  targetHeight?: number
): Promise<string> {
  const finalHeight = targetHeight ?? targetWidth;
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      canvas.width = targetWidth;
      canvas.height = finalHeight;

      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        targetWidth,
        finalHeight
      );

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to create blob'));
            return;
          }

          const reader = new FileReader();
          reader.onloadend = () => {
            resolve(reader.result as string);
          };
          reader.onerror = () => reject(new Error('Failed to read blob'));
          reader.readAsDataURL(blob);
        },
        'image/jpeg',
        0.9
      );
    };

    image.onerror = () => reject(new Error('Failed to load image'));
    image.src = imageSrc;
  });
}

/**
 * Calculate byte size of Base64 string
 */
export function getBase64Size(base64String: string): number {
  // Remove data URL prefix to get just the Base64 data
  const base64Data = base64String.split(',')[1] || base64String;

  // Calculate size: (Base64 length * 3) / 4
  // Subtract padding characters if present
  const padding = (base64Data.match(/=/g) || []).length;
  return (base64Data.length * 3) / 4 - padding;
}

/**
 * Format bytes to human-readable size
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}
